import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/Bits";
import { SECTIONS, JOURNEY, SUPPORT_EMAIL } from "@/lib/helpContent";
import { NarratedByLottie } from "@/components/NarratedByLottie";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Lightbulb, ArrowRight, EnvelopeSimple, CaretDown, WarningCircle,
  FilePdf, DownloadSimple, CircleNotch, MagnifyingGlass, X, Sparkle, PlayCircle,
} from "@phosphor-icons/react";

// Split an anchor ("Getting set up-0") into its section tag + index (tags contain hyphens).
function splitAnchor(anchor) {
  const d = anchor.lastIndexOf("-");
  return { tag: anchor.slice(0, d), i: parseInt(anchor.slice(d + 1), 10) };
}

// Match help guides to videos tolerant of "&"/"and", "@", punctuation drift.
const normTitle = (t) =>
  (t || "").toLowerCase().replace(/&/g, "and").replace(/@/g, "")
    .replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

function GuideBody({ guide, video }) {
  const [showDetail, setShowDetail] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const hasDetail = !!(guide.detail && (guide.detail.walkthrough?.length || guide.detail.gotchas?.length));
  return (
    <div className="pb-6">
      {video && !videoFailed && (
        <div className="ml-12 mb-4 max-w-xl" data-testid={`help-video-${video.slug}`}>
          <div className="rounded-xl overflow-hidden bg-navy border border-slate-200 shadow-sm">
            <video
              src={`${API}${video.video_url}`}
              poster={video.has_thumb ? `${API}${video.thumb_url}` : undefined}
              controls playsInline preload="none"
              controlsList="nodownload noremoteplayback noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              onError={() => setVideoFailed(true)}
              className="w-full aspect-video bg-black"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-navy/55">
              <PlayCircle size={15} weight="fill" style={{ color: "#F37A24" }} />
              Watch the narrated walkthrough for this step.
            </span>
            <NarratedByLottie />
          </div>
        </div>
      )}
      {video && videoFailed && (
        <div className="ml-12 mb-4 max-w-xl" data-testid={`help-video-unavailable-${video.slug}`}>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            The narrated walkthrough for this step is temporarily unavailable. The written steps below cover exactly the same thing.
          </div>
        </div>
      )}
      <ol className="list-decimal pl-12 space-y-2 text-sm text-slate-600 leading-relaxed">
        {guide.steps.map((step, si) => <li key={si}>{step}</li>)}
      </ol>

      {hasDetail && (
        <div className="ml-12 mt-4">
          <button
            data-testid="help-toggle-detail"
            onClick={() => setShowDetail((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink hover:text-orangedark transition-colors">
            <CaretDown size={14} weight="bold" className={`transition-transform ${showDetail ? "rotate-180" : ""}`} />
            {showDetail ? "Hide detailed walkthrough" : "Show detailed walkthrough"}
          </button>

          {showDetail && (
            <div data-testid="help-detail-panel" className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-4 fade-up">
              {guide.detail.walkthrough?.length > 0 && (
                <div>
                  <div className="overline text-navy/65 mb-2">Step by step</div>
                  <ul className="space-y-2">
                    {guide.detail.walkthrough.map((w, wi) => (
                      <li key={wi} className="flex gap-2.5 text-sm text-navy/75 leading-relaxed">
                        <ArrowRight size={15} weight="bold" className="text-orangeink shrink-0 mt-1" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {guide.detail.gotchas?.length > 0 && (
                <div>
                  <div className="overline text-navy/65 mb-2">Good to know</div>
                  <ul className="space-y-2">
                    {guide.detail.gotchas.map((g, gi) => (
                      <li key={gi} className="flex gap-2.5 text-sm text-navy/70 leading-relaxed">
                        <WarningCircle size={16} weight="duotone" style={{ color: "#F59E0B" }} className="shrink-0 mt-0.5" />
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {guide.tip && (
        <div className="flex items-start gap-2 mt-4 ml-12 text-sm rounded-lg p-3" style={{ background: "rgba(242,111,33,0.06)" }}>
          <Lightbulb size={17} weight="duotone" style={{ color: "#F37A24" }} className="shrink-0 mt-0.5" />
          <span className="text-navy/70">{guide.tip}</span>
        </div>
      )}
      {guide.to && (
        <Link to={guide.to} data-testid="help-cta-link"
          className="inline-flex items-center gap-1.5 mt-4 ml-12 text-sm font-semibold text-orangeink hover:gap-2.5 transition-all">
          {guide.cta} <ArrowRight size={15} weight="bold" />
        </Link>
      )}
    </div>
  );
}

export default function HelpCenter() {
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [aiMatches, setAiMatches] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [videoMap, setVideoMap] = useState({});

  useEffect(() => {
    api.get("/learning/walkthroughs")
      .then(({ data }) => {
        const map = {};
        (data.videos || []).forEach((v) => { map[normTitle(v.title)] = v; });
        setVideoMap(map);
      })
      .catch(() => {});
  }, []);

  const emailGuide = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const { data } = await api.post("/help/email-guide");
      toast.success(`Sent — check ${data.to} for your user guide.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email the guide. Please try again.");
    } finally {
      setEmailing(false);
    }
  };

  const downloadGuide = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("accrued_token");
      const res = await fetch(`${API}/help/user-guide.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "workwithlottie-user-guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* noop — user can retry */
    } finally {
      setDownloading(false);
    }
  };
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const [open, setOpen] = useState(focus ? [focus] : []);
  const scrolled = useRef(false);

  // On arrival with a ?focus= guide, scroll it into view once.
  useEffect(() => {
    if (focus && !scrolled.current) {
      scrolled.current = true;
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="help-guide-${focus}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
    }
  }, [focus]);

  // Record a guide as "read" for the Learning dashboard whenever it's expanded.
  const recordRead = (values) => {
    const newly = values.filter((v) => !open.includes(v));
    newly.forEach((anchor) => api.post("/learning/progress", { kind: "guide", key: anchor }).catch(() => {}));
  };

  const goToGuide = (tag, i) => {
    const value = `${tag}-${i}`;
    setQuery("");
    setOpen((cur) => (cur.includes(value) ? cur : [...cur, value]));
    api.post("/learning/progress", { kind: "guide", key: value }).catch(() => {});
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="help-guide-${value}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  // "Watch the walkthrough" chip: expand the guide, scroll to its embedded video and play it.
  const watchVideo = (e, value, video) => {
    e.stopPropagation();
    setQuery("");
    setOpen((cur) => (cur.includes(value) ? cur : [...cur, value]));
    api.post("/learning/progress", { kind: "guide", key: value }).catch(() => {});
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="help-video-${video.slug}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.querySelector("video")?.play?.().catch(() => {});
      }
    }, 220);
  };

  const runAiSearch = async () => {
    const q = query.trim();
    if (!q || aiLoading) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/help/search", { query: q });
      setAiMatches(data.matches || []);
    } catch {
      setAiMatches([]);
    } finally {
      setAiLoading(false);
    }
  };

  const openAiMatch = (m) => {
    const { tag, i } = splitAnchor(m.anchor);
    goToGuide(tag, i);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    const match = (g) =>
      [g.title, g.tip || "", ...g.steps,
        ...(g.detail?.walkthrough || []), ...(g.detail?.gotchas || [])]
        .join(" ").toLowerCase().includes(q);
    return SECTIONS
      .map((s) => ({ ...s, guides: s.guides.filter(match) }))
      .filter((s) => s.guides.length);
  }, [query]);

  const openValues = useMemo(() => {
    if (!query.trim()) return null;
    return filtered.flatMap((s) => s.guides.map((g) => `${s.tag}-${s.guides.indexOf(g)}`));
  }, [query, filtered]);

  const searching = !!query.trim();
  const resultCount = filtered.reduce((n, s) => n + s.guides.length, 0);

  return (
    <Page>
      <PageHeader
        overline="Help Centre"
        title="How to get the most from Lottie"
        subtitle="Follow the journey from your first Xero connection through every regular task to month-end close and reporting. Simple steps up front — expand any guide for the full detail."
      />

      {/* Guided in-app tours live in the Learning Centre (which tracks completion) */}
      <div data-testid="user-guide-banner"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-navy text-white shadow-card p-5 mb-6 fade-up">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.18)" }}>
            <FilePdf size={22} weight="duotone" style={{ color: "#F37A24" }} />
          </div>
          <div>
            <h3 className="font-heading font-bold">Download the full user guide (PDF)</h3>
            <p className="text-white/60 text-sm mt-0.5">A branded, chapter-by-chapter guide mirroring everything below — quick steps with detailed instructions. Perfect for onboarding or sharing with your team.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
        <button onClick={downloadGuide} disabled={downloading} data-testid="download-user-guide-btn"
          className="shrink-0 inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-xl text-white transition-colors disabled:opacity-70"
          style={{ background: "#F37A24" }}>
          {downloading ? <CircleNotch size={18} weight="bold" className="animate-spin" /> : <DownloadSimple size={18} weight="bold" />}
          {downloading ? "Preparing…" : "Download guide"}
        </button>
        <button onClick={emailGuide} disabled={emailing} data-testid="email-user-guide-btn"
          className="shrink-0 inline-flex items-center justify-center gap-2 font-medium px-4 py-3 rounded-xl text-white/90 border border-white/25 hover:bg-white/10 transition-colors disabled:opacity-70">
          {emailing ? <CircleNotch size={17} weight="bold" className="animate-spin" /> : <EnvelopeSimple size={17} weight="bold" />}
          {emailing ? "Sending…" : "Email it to me"}
        </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-2 fade-up">
        <MagnifyingGlass size={19} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
        <input
          data-testid="help-search-input"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setAiMatches(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runAiSearch(); } }}
          placeholder="Search the help centre… ask in your own words, then press Enter for AI matches"
          className="w-full rounded-xl border border-slate-200 bg-white shadow-card pl-12 pr-40 py-3.5 text-sm text-navy placeholder:text-slate-600 outline-none focus:border-orange transition-colors"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {query && (
            <button onClick={() => { setQuery(""); setAiMatches(null); }} data-testid="help-search-clear"
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-navy transition-colors">
              <X size={16} weight="bold" />
            </button>
          )}
          <button onClick={runAiSearch} disabled={!query.trim() || aiLoading} data-testid="help-ai-search-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-2 rounded-lg transition-colors disabled:opacity-40"
            style={{ background: "#0B1A2F" }}>
            {aiLoading ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Sparkle size={14} weight="fill" style={{ color: "#F37A24" }} />}
            AI search
          </button>
        </div>
      </div>

      {/* AI semantic matches */}
      {aiMatches && (
        <div data-testid="help-ai-results" className="rounded-2xl border border-navy/15 bg-navy/[0.03] p-5 mb-6 fade-up">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="fill" style={{ color: "#F37A24" }} />
            <h3 className="font-heading font-semibold text-navy text-sm">AI-matched guides for “{query.trim()}”</h3>
          </div>
          {aiMatches.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="help-ai-empty">No close matches — try the keyword results below, or email support.</p>
          ) : (
            <div className="grid gap-2">
              {aiMatches.map((m) => (
                <button key={m.id} onClick={() => openAiMatch(m)} data-testid={`help-ai-match-${m.id}`}
                  className="group text-left rounded-xl border border-slate-200 bg-white hover:border-orange/50 hover:shadow-sm transition-all px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy text-sm">{m.title}</div>
                    {m.reason && <div className="text-xs text-slate-500 mt-0.5">{m.reason}</div>}
                  </div>
                  <ArrowRight size={16} weight="bold" className="text-slate-300 group-hover:text-orangeink group-hover:translate-x-0.5 transition-all shrink-0 mt-0.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Workflow roadmap — hidden while searching */}
      {!searching && (
        <div data-testid="help-roadmap" className="rounded-2xl border border-slate-200 bg-white shadow-card p-6 mb-8 mt-6 fade-up">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-heading font-bold text-navy text-lg">Your Lottie workflow</h2>
          </div>
          <p className="text-slate-600 text-sm mb-6">The full lifecycle, start to finish. Click any step to jump straight to its guide.</p>

          <div className="grid gap-6 md:grid-cols-3">
            {JOURNEY.map((phase, pi) => (
              <div key={phase.phase} data-testid={`roadmap-phase-${pi}`} className="relative">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.1)" }}>
                    <phase.icon size={19} weight="duotone" style={{ color: "#F37A24" }} />
                  </span>
                  <div>
                    <div className="overline text-orangeink leading-none">Phase {pi + 1}</div>
                    <div className="font-heading font-semibold text-navy leading-tight">{phase.phase}</div>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mb-3 leading-relaxed">{phase.blurb}</p>
                <ol className="space-y-1.5">
                  {phase.steps.map((st, si) => (
                    <li key={st.label}>
                      <button
                        data-testid={`roadmap-step-${pi}-${si}`}
                        onClick={() => goToGuide(st.tag, st.i)}
                        className="w-full group flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50/60 hover:bg-white hover:border-orange/40 px-3 py-2 text-left transition-colors">
                        <span className="w-5 h-5 rounded-full bg-navy text-white text-[0.65rem] font-bold flex items-center justify-center shrink-0">
                          {si + 1}
                        </span>
                        <span className="text-sm text-navy/80 font-medium flex-1">{st.label}</span>
                        <ArrowRight size={14} weight="bold" className="text-slate-300 group-hover:text-orangeink group-hover:translate-x-0.5 transition-all" />
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}

      {query.trim() && (
        <div className="text-sm text-slate-600 mb-6" data-testid="help-search-count">
          {resultCount} {resultCount === 1 ? "guide" : "guides"} matching “{query.trim()}”
        </div>
      )}

      {/* Support card */}
      <div data-testid="help-support-card"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white shadow-card p-6 mb-8 fade-up">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.1)" }}>
            <EnvelopeSimple size={22} weight="duotone" style={{ color: "#F37A24" }} />
          </div>
          <div>
            <h3 className="font-heading font-bold text-navy">Still need a hand?</h3>
            <p className="text-slate-600 text-sm mt-0.5">Email our team and a real person will help you out — usually within a few hours.</p>
          </div>
        </div>
        <a href={`mailto:${SUPPORT_EMAIL}?subject=WorkWithLottie%20support`} data-testid="help-contact-support"
          className="shrink-0 inline-flex items-center justify-center gap-2 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
          style={{ background: "#F37A24" }}>
          Contact support <ArrowRight size={17} weight="bold" />
        </a>
      </div>

      {resultCount === 0 ? (
        <div data-testid="help-no-results" className="text-center py-16 rounded-2xl border border-dashed border-slate-200 bg-white">
          <MagnifyingGlass size={28} weight="duotone" className="mx-auto text-slate-300 mb-3" />
          <p className="text-navy font-medium">No guides matched “{query.trim()}”.</p>
          <p className="text-slate-600 text-sm mt-1">Try the AI search above, a different term, or <a href={`mailto:${SUPPORT_EMAIL}?subject=WorkWithLottie%20support`} className="font-semibold text-orangeink">email support</a>.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {filtered.map((s) => (
            <div key={s.tag} data-testid={`help-section-${s.tag}`}>
              <h2 className="overline text-orangeink mb-3">{s.tag}</h2>
              <Accordion
                type="multiple"
                value={searching ? openValues : open}
                onValueChange={searching ? () => {} : (v) => { recordRead(v); setOpen(v); }}
                className="bg-white rounded-2xl border border-slate-200 shadow-card px-5"
              >
                {s.guides.map((g, i) => {
                  const value = `${s.tag}-${i}`;
                  const gVideo = videoMap[normTitle(g.title)];
                  return (
                  <AccordionItem key={g.title} value={value} className="border-slate-100 last:border-0"
                    data-testid={`help-guide-${s.tag}-${i}`}>
                    <AccordionTrigger className="hover:no-underline py-5">
                      <span className="flex items-center gap-3 text-left flex-1 min-w-0">
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(46,163,253,0.1)" }}>
                          <g.icon size={18} weight="duotone" className="text-orangeink" />
                        </span>
                        <span className="font-heading font-semibold text-navy">{g.title}</span>
                        {gVideo && (
                          <span role="button" tabIndex={0}
                            data-testid={`help-watch-chip-${value}`}
                            onClick={(e) => watchVideo(e, value, gVideo)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") watchVideo(e, value, gVideo); }}
                            className="ml-1 inline-flex items-center gap-1 text-[0.68rem] font-semibold px-2.5 py-1 rounded-full shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                            style={{ background: "rgba(242,111,33,0.1)", color: "#C2410C" }}>
                            <PlayCircle size={14} weight="fill" /> Watch the walkthrough
                          </span>
                        )}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <GuideBody guide={g} video={gVideo} />
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
